import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { listPlatformDominioUsageEvents } from "../../../api/client";
import { getAiCreditAuditLabel } from "../../../utils/aiCreditAuditLabel";

function fmtDateShort(v, localeTag = "pt-BR") {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString(localeTag, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function rowQuantity(row) {
  return Number(row?.quantity ?? row?.credits ?? 0);
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string|number} props.dominioId
 * @param {string} props.metricKey
 * @param {{ from?: string, to?: string } | null} props.period
 * @param {number | null} props.displayTotal — total exibido no card (usage_daily_aggregates)
 * @param {string} [props.title]
 */
export default function UsageMetricAuditDrilldownDialog({
  open,
  onClose,
  dominioId,
  metricKey = "ai_credits",
  period,
  displayTotal = null,
  title = "",
}) {
  const { t, i18n } = useTranslation("app");
  const locale = i18n?.language || "pt-BR";
  const mk = String(metricKey || "ai_credits").trim();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [page, setPage] = useState(0);
  const pageSize = 50;

  const auditLabel = useCallback((kind, key, fallback) => {
    const label = getAiCreditAuditLabel(kind, key || fallback, t);
    if (label !== "—") return label;
    return fallback || String(key || "").replace(/_/g, " ");
  }, [t]);

  const quantityColumnLabel = useMemo(() => {
    if (mk === "ai_credits") return t("platformPage.aiCreditAuditCredits");
    return t("platformPage.usageMetricAuditQuantity");
  }, [mk, t]);

  const load = useCallback(
    async (pageIndex = 0) => {
      if (!dominioId || !open || !mk) return;
      setLoading(true);
      try {
        const params = { metricKey: mk, limit: pageSize, offset: pageIndex * pageSize };
        if (period?.from) params.from = period.from;
        if (period?.to) params.to = period.to;
        const res = await listPlatformDominioUsageEvents(dominioId, params);
        setData(res);
        setPage(pageIndex);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [dominioId, open, period?.from, period?.to, mk]
  );

  useEffect(() => {
    if (open) load(0);
  }, [open, load]);

  const periodLabel = useMemo(() => {
    if (!period?.from || !period?.to) return "";
    return t("billingPage.usagePeriod", { from: period.from, to: period.to });
  }, [period?.from, period?.to, t]);

  const eventsSum = Number(data?.quantity_sum ?? data?.credits_sum ?? 0);
  const eventCount = Number(data?.total ?? 0);
  const shownTotal = displayTotal != null ? Number(displayTotal) : null;
  const reconcileMismatch =
    shownTotal != null && eventCount > 0 && eventsSum !== shownTotal && Math.abs(eventsSum - shownTotal) > 0;

  const defaultTitle = t("platformPage.usageMetricAuditDrilldownTitle", {
    defaultValue: t("platformPage.aiCreditAuditDrilldownTitle"),
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" component="span" fontWeight={700}>
          {title || defaultTitle}
        </Typography>
        {periodLabel ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {periodLabel}
          </Typography>
        ) : null}
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 1 }}>
        <Stack spacing={2}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
              gap: 1.5,
            }}
          >
            {shownTotal != null ? (
              <Box sx={{ p: 1.5, borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t("platformPage.aiCreditAuditDrilldownCardTotal")}
                </Typography>
                <Typography variant="h6" fontWeight={700} sx={{ fontVariantNumeric: "tabular-nums" }}>
                  {shownTotal.toLocaleString(locale)}
                </Typography>
              </Box>
            ) : null}
            <Box sx={{ p: 1.5, borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
              <Typography variant="caption" color="text.secondary" display="block">
                {t("platformPage.aiCreditAuditDrilldownEventsSum")}
              </Typography>
              <Typography variant="h6" fontWeight={700} sx={{ fontVariantNumeric: "tabular-nums" }}>
                {eventsSum.toLocaleString(locale)}
              </Typography>
            </Box>
            <Box sx={{ p: 1.5, borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
              <Typography variant="caption" color="text.secondary" display="block">
                {t("platformPage.aiCreditAuditDrilldownEventCount")}
              </Typography>
              <Typography variant="h6" fontWeight={700} sx={{ fontVariantNumeric: "tabular-nums" }}>
                {eventCount.toLocaleString(locale)}
              </Typography>
            </Box>
          </Box>

          {data?.schema_missing ? (
            <Alert severity="warning">{t("platformPage.usageMetricAuditSchemaMissing")}</Alert>
          ) : null}

          {reconcileMismatch ? (
            <Alert severity="info">
              {t("platformPage.aiCreditAuditDrilldownReconcile", {
                card: shownTotal.toLocaleString(locale),
                events: eventsSum.toLocaleString(locale),
              })}
            </Alert>
          ) : null}

          {loading && !data?.items?.length ? (
            <Stack alignItems="center" py={4}>
              <CircularProgress size={32} />
            </Stack>
          ) : data?.items?.length ? (
            <>
              <TableContainer sx={{ maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("platformPage.aiCreditAuditWhen")}</TableCell>
                      <TableCell align="right">{quantityColumnLabel}</TableCell>
                      <TableCell>{t("platformPage.aiCreditAuditFeature")}</TableCell>
                      <TableCell>{t("platformPage.aiCreditAuditSource")}</TableCell>
                      <TableCell>{t("platformPage.aiCreditAuditUser")}</TableCell>
                      <TableCell>{t("platformPage.aiCreditAuditContext")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.items.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDateShort(row.created_at, locale)}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                          {rowQuantity(row).toLocaleString(locale)}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 120, wordBreak: "break-word" }}>
                          {row.feature_kind
                            ? auditLabel("feature", row.feature_kind, row.feature_kind)
                            : "—"}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 160, wordBreak: "break-word", fontSize: "0.8rem" }}>
                          {auditLabel("source", row.source_key, row.source_key)}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 140, wordBreak: "break-word" }}>
                          {row.user_id
                            ? `${row.user_email || `#${row.user_id}`}${row.user_role ? ` (${row.user_role})` : ""}`
                            : t("platformPage.aiCreditAuditUserSystem")}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 180, wordBreak: "break-word", fontSize: "0.8rem" }}>
                          {[
                            row.conversation_id
                              ? `${t("platformPage.aiCreditAuditConversation")} ${row.conversation_id}`
                              : null,
                            row.agent_id ? `agent ${row.agent_id}` : null,
                            row.session_id ? `sess. ${row.session_id}` : null,
                            row.model ? row.model : null,
                          ]
                            .filter(Boolean)
                            .join(" · ") || "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="caption" color="text.secondary">
                  {t("platformPage.aiCreditAuditTotal", {
                    shown: data.items.length,
                    total: eventCount.toLocaleString(locale),
                  })}
                </Typography>
                {page > 0 ? (
                  <Button size="small" disabled={loading} onClick={() => load(page - 1)}>
                    {t("platformPage.aiCreditAuditPrev")}
                  </Button>
                ) : null}
                {(page + 1) * pageSize < eventCount ? (
                  <Button size="small" disabled={loading} onClick={() => load(page + 1)}>
                    {t("platformPage.aiCreditAuditNext")}
                  </Button>
                ) : null}
              </Stack>
            </>
          ) : !loading ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              {t("platformPage.usageMetricAuditEmpty")}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => load(page)} disabled={loading}>
          {t("billingPage.refresh")}
        </Button>
        <Button onClick={onClose} variant="contained">
          {t("platformPage.close", { defaultValue: "Fechar" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Alias legado */
export { UsageMetricAuditDrilldownDialog as AiCreditAuditDrilldownDialog };

/** Número clicável para abrir auditoria da métrica */
export function ClickableUsageAmount({ metricKey, value, locale, onOpen, sx }) {
  const { t } = useTranslation("app");
  const n = Number(value || 0);
  const formatted = n.toLocaleString(locale);

  if (!onOpen) {
    return (
      <Typography variant="body1" fontWeight={700} sx={{ fontVariantNumeric: "tabular-nums", flexShrink: 0, ...sx }}>
        {formatted}
      </Typography>
    );
  }

  return (
    <Link
      component="button"
      type="button"
      variant="body1"
      fontWeight={700}
      onClick={onOpen}
      sx={{
        fontVariantNumeric: "tabular-nums",
        flexShrink: 0,
        cursor: "pointer",
        textAlign: "inherit",
        border: "none",
        background: "none",
        font: "inherit",
        ...sx,
      }}
      title={t("platformPage.usageMetricAuditClickHint")}
    >
      {formatted}
    </Link>
  );
}

/** @deprecated use ClickableUsageAmount */
export const ClickableAiCreditsAmount = ClickableUsageAmount;
